import { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { usePeopleProfiles, PeopleProfile } from '@/hooks/usePeopleProfiles';
import { PeopleProfileCard, CarouselNav, CarouselDots, Tag, PeopleProfileDialog } from '@/components/ds';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSectionHeader } from '@/hooks/useSectionHeaders';
import { RenderHeaderParts } from '@/components/ds/RenderCMSHeader';
import { useSectionVisibility } from '@/hooks/useSectionVisibility';
import { useCarouselTracking } from '@/hooks/useCarouselTracking';
import { useContentHover } from '@/hooks/useContentHover';

// ⚠️ ANALYTICS: Card hover wrapper for people profile cards
const PersonCardWrapper = ({ person, index, children }: {
  person: { id: string; name: string };
  index: number;
  children: React.ReactNode;
}) => {
  const { contentHoverProps } = useContentHover({
    contentId: person.id,
    contentType: 'card',
    contentTitle: person.name,
    contentSection: 'people',
    contentPage: 'about',
    contentPosition: index,
  });

  return <div {...contentHoverProps}>{children}</div>;
};

interface PeopleGroup {
  title: string;
  people: PeopleProfile[];
}

function PersonCarousel({ group, isVisible }: { group: PeopleGroup; isVisible: boolean }) {
  const [selectedPerson, setSelectedPerson] = useState<PeopleProfile | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isMobile = useIsMobile();
  
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
    // Mobile: require clearer horizontal intent before grabbing gesture
    dragThreshold: 10,
    breakpoints: {
      '(min-width: 768px)': { dragThreshold: 1 },
    },
  });

  // ⚠️ ANALYTICS: Carousel tracking
  const {
    trackCarouselViewed,
    trackCarouselScrolled,
  } = useCarouselTracking({
    carouselId: `people-${group.title.toLowerCase().replace(/\s+/g, '-')}`,
    section: 'people',
    pageName: 'About',
    totalItems: group.people.length,
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();

    // ⚠️ ANALYTICS: Track carousel scroll
    if (newIndex !== selectedIndex) {
      trackCarouselScrolled(selectedIndex, newIndex, 'drag');
    }

    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(newIndex);
  }, [emblaApi, selectedIndex, trackCarouselScrolled]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // ⚠️ ANALYTICS: Track carousel view when section is visible
  useEffect(() => {
    if (isVisible && emblaApi && group.people.length > 0) {
      const visibleCount = emblaApi.slidesInView().length;
      trackCarouselViewed(visibleCount);
    }
  }, [isVisible, emblaApi, group.people.length, trackCarouselViewed]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollToIndex = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  if (group.people.length === 0) {
    return null;
  }

  return (
    <div className="mb-16 last:mb-0">
      {/* Group Header with CarouselNav */}
      <div className="container mx-auto px-4 md:px-8 mb-6">
        <div className="flex items-center justify-between">
          {/* atom-text-section-header-bold */}
          <h3 className="text-2xl md:text-3xl font-bold text-foreground">
            {group.title}
          </h3>
          {/* molecule-carousel-nav - desktop only */}
          {!isMobile && (
            <CarouselNav
              onPrev={scrollPrev}
              onNext={scrollNext}
              canScrollPrev={canScrollPrev}
              canScrollNext={canScrollNext}
              variant="subdued"
            />
          )}
        </div>
      </div>

      {/* Carousel */}
      <div className="pl-4 md:pl-8 lg:pl-[max(2rem,calc((100vw-1400px)/2+2rem))]">
        <div className="overflow-visible" ref={emblaRef}>
          <div className="flex gap-4 embla-gesture-lock">
            {group.people.map((person, index) => (
              <PersonCardWrapper key={person.id} person={person} index={index}>
                <div
                  className={`flex-shrink-0 ${
                    index === group.people.length - 1
                      ? 'mr-4 md:mr-8 lg:mr-[max(2rem,calc((100vw-1400px)/2+2rem))]'
                      : ''
                  }`}
                >
                  {/* component-card-people */}
                  {/* ⚠️ ANALYTICS: MODAL_OPENED is fired inside PeopleProfileDialog (FIX 4 – de-duplicated) */}
                  <PeopleProfileCard
                    imageUrl={person.image_url || undefined}
                    name={person.name}
                    title={person.title}
                    onClick={() => setSelectedPerson(person)}
                  />
                </div>
              </PersonCardWrapper>
            ))}
          </div>
        </div>
      </div>

      {/* molecule-carousel-dots - mobile only */}
      {isMobile && (
        <div className="mt-6 px-4">
          <CarouselDots
            selectedIndex={selectedIndex}
            totalSlides={group.people.length}
            onDotClick={scrollToIndex}
          />
        </div>
      )}

      {/* component-dialog-people (reusable) */}
      <PeopleProfileDialog
        profile={selectedPerson}
        open={!!selectedPerson}
        onOpenChange={(open) => !open && setSelectedPerson(null)}
      />
    </div>
  );
}

export default function PeopleSection() {
  const { data: peopleProfiles, isLoading } = usePeopleProfiles();
  
  // CMS data - note: uses 'people' key to match frontend ID
  const sectionHeader = useSectionHeader('about', 'people');

  // ⚠️ ANALYTICS: Section visibility tracking
  const { ref: sectionRef, inView: sectionInView } = useSectionVisibility({
    sectionId: 'people',
    sectionName: 'Our People',
    pageName: 'About',
    position: 5,
  });

  const boardMembers = peopleProfiles?.filter(p => p.category === 'board' && p.is_active) || [];
  const executives = peopleProfiles?.filter(p => p.category === 'executive' && p.is_active) || [];

  const peopleGroups: PeopleGroup[] = [
    { title: 'Board of Directors', people: boardMembers },
    { title: 'Private Markets Executive', people: executives },
  ];

  const hasAnyProfiles = boardMembers.length > 0 || executives.length > 0;

  if (isLoading) {
    return (
      <section id="people" className="py-24 md:py-32 bg-platinum">
        <div className="container mx-auto px-4 md:px-8">
          <div className="animate-pulse">Loading...</div>
        </div>
      </section>
    );
  }

  if (!hasAnyProfiles) {
    return null;
  }

  return (
    <section id="people" ref={sectionRef} className="py-24 md:py-32 bg-platinum overflow-hidden">
      {/* molecule-section-header-with-tag */}
      <div className="container mx-auto px-4 md:px-8 mb-12">
        {/* atom-tag-subtle */}
        {sectionHeader?.tag_text && (
          <div className="mb-6">
            <Tag variant="subtle">{sectionHeader.tag_text}</Tag>
          </div>
        )}
        {/* atom-text-section-header + atom-text-section-header-emphasis */}
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-foreground max-w-3xl mb-4">
          <RenderHeaderParts text={sectionHeader?.header_text || 'Led by **experienced professionals.**'} />
        </h2>
        {/* atom-text-section-subheader */}
        {sectionHeader?.sub_header && (
          <p className="text-17 text-muted-foreground max-w-2xl">
            {sectionHeader.sub_header}
          </p>
        )}
      </div>

      {/* People Carousels */}
      {peopleGroups.map((group) => (
        <PersonCarousel key={group.title} group={group} isVisible={sectionInView} />
      ))}

      {/* SEO: Hidden content block — exposes all team profile dialog content to crawlers */}
      {peopleProfiles && peopleProfiles.filter(p => p.is_active).length > 0 && (
        <div className="sr-only">
          {peopleProfiles.filter(p => p.is_active).map((person) => (
            <div key={`seo-${person.id}`}>
              <h3>{person.name} — {person.title}</h3>
              {person.bio && <p>{person.bio}</p>}
              {person.byline && <p>{person.byline}</p>}
              {person.years_experience && <p>{person.years_experience} years of experience</p>}
              {person.specialisations.length > 0 && (
                <div>
                  <h4>Specialisations</h4>
                  <ul>{person.specialisations.map(s => <li key={s.id}>{s.specialisation}</li>)}</ul>
                </div>
              )}
              {person.experiences.length > 0 && (
                <div>
                  <h4>Experience</h4>
                  <ul>{person.experiences.map(e => <li key={e.id}>{e.experience}</li>)}</ul>
                </div>
              )}
              {person.credentials.length > 0 && (
                <div>
                  <h4>Credentials</h4>
                  <ul>{person.credentials.map(c => <li key={c.id}>{c.credential}</li>)}</ul>
                </div>
              )}
              {person.education.length > 0 && (
                <div>
                  <h4>Education</h4>
                  <ul>{person.education.map(e => <li key={e.id}>{e.education}</li>)}</ul>
                </div>
              )}
              {person.linkedin_url && (
                <a href={person.linkedin_url} rel="noopener noreferrer">
                  {person.name} on LinkedIn
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
